import { CircleAlertIcon, EyeOffIcon } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Textarea } from "../ui/textarea";
import { parseVmSecrets, type VmSecretDraftRow, type VmSecretEntry } from "./VmSecretsDialog.logic";

type VmSecretsDialogProps = {
  readonly existingSecretNames?: ReadonlyArray<string>;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSave: (entries: ReadonlyArray<VmSecretEntry>) => Promise<void> | void;
};

const NO_EXISTING_SECRET_NAMES: ReadonlyArray<string> = [];

function SecretValueMask() {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <EyeOffIcon aria-hidden className="size-3.5" />
      <span aria-hidden className="font-mono tracking-wider">
        ••••••••
      </span>
      <span className="sr-only">Secret value hidden</span>
    </span>
  );
}

function SecretRowStatus({ row }: { readonly row: VmSecretDraftRow }) {
  if (row.error === null) return <span className="text-muted-foreground">Ready</span>;
  return (
    <span className="inline-flex max-w-72 items-start gap-1.5 whitespace-normal text-destructive">
      <CircleAlertIcon aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      <span>{row.error.message}</span>
    </span>
  );
}

export function VmSecretPreviewTable({
  existingSecretNames,
  rows,
}: {
  readonly existingSecretNames: ReadonlyArray<string>;
  readonly rows: ReadonlyArray<VmSecretDraftRow>;
}) {
  const configuredSecretNameSet = new Set(existingSecretNames);
  return (
    <div className="overflow-hidden rounded-lg border border-border/70">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Name</TableHead>
            <TableHead>Value</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={3} className="h-16 text-center text-muted-foreground">
                Paste one or more secrets above.
              </TableCell>
            </TableRow>
          ) : null}
          {rows.map((row) => (
            <TableRow key={`line:${row.lineNumber}`} aria-invalid={row.error !== null || undefined}>
              <TableCell>
                {row.name.length > 0 ? (
                  <code className="break-all whitespace-normal">{row.name}</code>
                ) : (
                  <span className="text-muted-foreground">Line {row.lineNumber}</span>
                )}
              </TableCell>
              <TableCell>
                <SecretValueMask />
              </TableCell>
              <TableCell>
                {row.error === null && configuredSecretNameSet.has(row.name) ? (
                  <span className="text-muted-foreground">Will update</span>
                ) : (
                  <SecretRowStatus row={row} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function VmSecretsDialog({
  existingSecretNames = NO_EXISTING_SECRET_NAMES,
  open,
  onOpenChange,
  onSave,
}: VmSecretsDialogProps) {
  const [input, setInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const inputId = useId();
  const hintId = useId();
  const validationId = useId();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const visibleInput = open ? input : "";
  const parsed = useMemo(() => parseVmSecrets(visibleInput), [visibleInput]);

  const reset = useCallback(() => {
    setInput("");
    setSubmitted(false);
    setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isSaving) return;
      if (!nextOpen) reset();
      onOpenChange(nextOpen);
    },
    [isSaving, onOpenChange, reset],
  );

  const save = useCallback(async () => {
    setSubmitted(true);
    setSaveError(null);
    if (parsed.rows.length === 0 || parsed.errorCount > 0) {
      inputRef.current?.focus();
      return;
    }

    setIsSaving(true);
    try {
      await onSave(parsed.entries);
      reset();
      onOpenChange(false);
    } catch {
      setSaveError("Unable to save secrets. Try again.");
    } finally {
      setIsSaving(false);
    }
  }, [onOpenChange, onSave, parsed.entries, parsed.errorCount, parsed.rows.length, reset]);

  const validationMessage =
    parsed.errorCount > 0
      ? `Fix ${parsed.errorCount} ${parsed.errorCount === 1 ? "secret" : "secrets"} before saving.`
      : parsed.rows.length > 0
        ? `${parsed.rows.length} ${parsed.rows.length === 1 ? "secret" : "secrets"} ready.`
        : submitted
          ? "Paste at least one secret in NAME=value format."
          : "";
  const hasValidationError = parsed.errorCount > 0 || (submitted && parsed.rows.length === 0);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPopup className="max-w-2xl overflow-hidden" showCloseButton={!isSaving}>
        <DialogHeader>
          <DialogTitle>Configure VM secrets</DialogTitle>
          <DialogDescription>
            Paste a single NAME=value assignment or a multiline .env block. Values disappear from
            this form after saving.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={inputId}>Secrets</Label>
            <Textarea
              ref={inputRef}
              id={inputId}
              value={visibleInput}
              rows={5}
              spellCheck={false}
              autoComplete="off"
              aria-invalid={hasValidationError || undefined}
              aria-describedby={`${hintId} ${validationId}`}
              placeholder={'DATABASE_URL="postgres://…"\nAPI_TOKEN=…'}
              className="font-mono"
              onChange={(event) => {
                setInput(event.target.value);
                setSubmitted(false);
                setSaveError(null);
              }}
            />
            <p id={hintId} className="text-xs text-muted-foreground">
              Supports export, quoted values, blank lines, and # comments. Each assignment becomes a
              separate row.
            </p>
            <p
              id={validationId}
              className={
                hasValidationError ? "text-xs text-destructive" : "text-xs text-muted-foreground"
              }
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {validationMessage}
            </p>
          </div>

          <VmSecretPreviewTable existingSecretNames={existingSecretNames} rows={parsed.rows} />

          {saveError ? (
            <p className="text-sm text-destructive" role="alert">
              {saveError}
            </p>
          ) : null}
        </DialogPanel>
        <DialogFooter>
          <Button variant="ghost" disabled={isSaving} onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={() => void save()}>
            {isSaving ? "Saving…" : "Save secrets"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

export type { VmSecretEntry, VmSecretsDialogProps };
